import React, { useState, useEffect } from 'react';
import { Student } from '../../types';
import { X, Plus, Users, GraduationCap, Eye, EyeOff, Upload, Image as ImageIcon, AlertCircle, RefreshCw, CheckCircle } from 'lucide-react';
import { uploadStudentImage, deleteStudentImage } from '../../services';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useToast } from '../../contexts/ToastContext';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Select from '../ui/Select';

interface StudentFormProps {
    student?: Student;
    onSubmit: (student: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>) => void;
    onCancel: () => void;
    loading?: boolean;
}

const StudentForm: React.FC<StudentFormProps> = ({ student, onSubmit, onCancel, loading = false }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [checkingStudentId, setCheckingStudentId] = useState(false);
    const [studentIdError, setStudentIdError] = useState('');
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const { showError, showSuccess } = useToast();

    const [formData, setFormData] = useState({
        studentId: student?.studentId || '',
        firstName: student?.firstName || '',
        lastName: student?.lastName || '',
        email: student?.email || '',
        phone: student?.phone || '',
        dateOfBirth: student?.dateOfBirth ? student.dateOfBirth.toISOString().split('T')[0] : '',
        gender: student?.gender || 'male',
        password: '',
        confirmPassword: '',
        profileImage: student?.profileImage || '',
        selectedImageFile: null as File | null,
        address: {
            street: student?.address.street || '',
            city: student?.address.city || '',
            state: student?.address.state || '',
            zipCode: student?.address.zipCode || '',
            country: student?.address.country || '',
        },
        parentInfo: {
            fatherName: student?.parentInfo.fatherName || '',
            fatherPhone: student?.parentInfo.fatherPhone || '',
            fatherEmail: student?.parentInfo.fatherEmail || '',
            fatherOccupation: student?.parentInfo.fatherOccupation || '',
            fatherWorkPlace: student?.parentInfo.fatherWorkPlace || '',
            motherName: student?.parentInfo.motherName || '',
            motherOccupation: student?.parentInfo.motherOccupation || '',
            motherWorkPlace: student?.parentInfo.motherWorkPlace || '',
        },
        academicInfo: {
            currentGrade: student?.academicInfo.currentGrade || '',
            section: student?.academicInfo.section || '',
            rollNumber: student?.academicInfo.rollNumber || '',
            admissionDate: student?.academicInfo.admissionDate ? student.academicInfo.admissionDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            status: student?.academicInfo.status || 'active',
            gradeHistory: student?.academicInfo.gradeHistory || [],
        },
        emergencyContact: {
            name: student?.emergencyContact.name || '',
            relationship: student?.emergencyContact.relationship || '',
            phone: student?.emergencyContact.phone || '',
            email: student?.emergencyContact.email || '',
        },
    });

    const [newGrade, setNewGrade] = useState({
        grade: '',
        academicYear: '',
        section: '',
        rollNumber: '',
        status: 'completed' as 'completed' | 'failed' | 'promoted',
        marks: {
            totalMarks: 0,
            obtainedMarks: 0,
            percentage: 0,
        },
        remarks: '',
    });
    const [studentIdValid, setStudentIdValid] = useState(false);
    const [emailValid, setEmailValid] = useState(false);

    // Auto-validate Student ID when user types (with debounce)
    useEffect(() => {
        if (!student && formData.studentId.trim()) {
            const timer = setTimeout(() => {
                checkStudentIdUnique(formData.studentId);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [formData.studentId]);

    // Auto-validate Email when user types (with debounce)
    useEffect(() => {
        if (!student && formData.email.trim()) {
            const timer = setTimeout(() => {
                checkEmailUnique(formData.email);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [formData.email]);

    // Check if student ID already exists
    const checkStudentIdUnique = async (studentId: string) => {
        if (!studentId.trim()) {
            setStudentIdError('');
            return true;
        }

        // Skip check if editing and ID hasn't changed
        if (student && student.studentId === studentId) {
            setStudentIdError('');
            return true;
        }

        try {
            setCheckingStudentId(true);
            const studentsRef = collection(db, 'students');
            const q = query(studentsRef, where('studentId', '==', studentId.trim()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setStudentIdError('This Student ID already exists. Please use a unique ID.');
                setStudentIdValid(false);
                return false;
            } else {
                setStudentIdError('');
                setStudentIdValid(true);
                return true;
            }
        } catch (error) {
            console.error('Error checking student ID:', error);
            setStudentIdError('Error validating Student ID. Please try again.');
            return false;
        } finally {
            setCheckingStudentId(false);
        }
    };

    // Check if email already exists
    const checkEmailUnique = async (email: string) => {
        if (!email.trim()) {
            setEmailError('');
            return true;
        }

        // Skip check if editing and email hasn't changed
        if (student && student.email === email) {
            setEmailError('');
            return true;
        }

        try {
            setCheckingEmail(true);
            const studentsRef = collection(db, 'students');
            const q = query(studentsRef, where('email', '==', email.trim().toLowerCase()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setEmailError('This email is already registered. Please use a different email.');
                setEmailValid(false);
                return false;
            } else {
                setEmailError('');
                setEmailValid(true);
                return true;
            }
        } catch (error) {
            console.error('Error checking email:', error);
            setEmailError('Error validating email. Please try again.');
            setEmailValid(false);
            return false;
        } finally {
            setCheckingEmail(false);
        }
    };

    // Store selected image file (upload on form submission)
    const handleImageUpload = (file: File) => {
        setFormData(prev => ({
            ...prev,
            selectedImageFile: file
        }));

        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
        showSuccess('Success', 'Image selected successfully! Will be uploaded when you submit the form.');
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                showError('Invalid File', 'Please select an image file.');
                return;
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                showError('File Too Large', 'Please select an image smaller than 5MB.');
                return;
            }

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);

            // Upload to Cloudinary
            handleImageUpload(file);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // Clear errors and valid states when user types
        if (name === 'studentId') {
            setStudentIdError('');
            setStudentIdValid(false);
        }
        if (name === 'email') {
            setEmailError('');
            setEmailValid(false);
        }

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...(prev[parent as keyof typeof prev] as any),
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Grade management functions
    const addGrade = () => {
        // Trim whitespace and check for empty strings
        const trimmedGrade = newGrade.grade.trim();
        const trimmedAcademicYear = newGrade.academicYear.trim();

        if (!trimmedGrade || !trimmedAcademicYear) {
            showError('Required Fields', 'Please fill in grade and academic year.');
            return;
        }

        const gradeData = {
            ...newGrade,
            grade: trimmedGrade,
            academicYear: trimmedAcademicYear,
            marks: {
                ...newGrade.marks,
                percentage: newGrade.marks.totalMarks > 0
                    ? Math.round((newGrade.marks.obtainedMarks / newGrade.marks.totalMarks) * 100 * 100) / 100
                    : 0
            },
            addedDate: new Date(),
        };

        setFormData(prev => ({
            ...prev,
            academicInfo: {
                ...prev.academicInfo,
                gradeHistory: [...prev.academicInfo.gradeHistory, gradeData]
            }
        }));

        // Reset form completely
        setNewGrade({
            grade: '',
            academicYear: '',
            section: '',
            rollNumber: '',
            status: 'completed',
            marks: {
                totalMarks: 0,
                obtainedMarks: 0,
                percentage: 0,
            },
            remarks: '',
        });

        // Show success message after a small delay to ensure state is updated
        setTimeout(() => {
            showSuccess('Success', 'Grade added successfully!');
        }, 100);
    };

    const removeGrade = (index: number) => {
        setFormData(prev => ({
            ...prev,
            academicInfo: {
                ...prev.academicInfo,
                gradeHistory: prev.academicInfo.gradeHistory.filter((_: any, i: number) => i !== index)
            }
        }));
    };

    const handleNewGradeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (name.includes('marks.')) {
            const field = name.split('.')[1];
            setNewGrade(prev => ({
                ...prev,
                marks: {
                    ...prev.marks,
                    [field]: parseFloat(value) || 0
                }
            }));
        } else {
            setNewGrade(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Student ID validation
        if (!formData.studentId.trim()) {
            showError('Student ID Required', 'Please enter a Student ID.');
            return;
        }

        // Check if Student ID is unique
        const isStudentIdUnique = await checkStudentIdUnique(formData.studentId);
        if (!isStudentIdUnique) {
            showError('Duplicate Student ID', 'This Student ID already exists. Please use a unique ID.');
            return;
        }

        // Email validation
        if (!formData.email.trim()) {
            showError('Email Required', 'Please enter an email address.');
            return;
        }

        // Check if Email is unique
        const isEmailUnique = await checkEmailUnique(formData.email);
        if (!isEmailUnique) {
            showError('Duplicate Email', 'This email is already registered. Please use a different email.');
            return;
        }

        // Password validation for new students
        if (!student) {
            if (!formData.password || formData.password.length < 6) {
                showError('Password Too Short', 'Password must be at least 6 characters long.');
                return;
            }
            if (formData.password !== formData.confirmPassword) {
                showError('Passwords Mismatch', 'Passwords do not match. Please check and try again.');
                return;
            }
        }

        // Helper function to extract public ID from Cloudinary URL
        const extractPublicId = (url: string): string | null => {
            const match = url.match(/\/v\d+\/(.+)\./);
            return match ? match[1] : null;
        };

        // Upload image if selected
        let profileImageUrl = formData.profileImage;
        if (formData.selectedImageFile) {
            setUploadingImage(true);
            try {
                // Delete old image if updating and has existing image
                if (student && student.profileImage) {
                    const oldPublicId = extractPublicId(student.profileImage);
                    if (oldPublicId) {
                        try {
                            await deleteStudentImage(oldPublicId);
                        } catch (deleteError) {
                            console.warn('Failed to delete old image:', deleteError);
                            // Continue with upload even if deletion fails
                        }
                    }
                }

                const result = await uploadStudentImage(formData.selectedImageFile, formData.studentId);
                profileImageUrl = result.secure_url;
            } catch (error) {
                console.error('Error uploading image:', error);
                showError('Error', 'Failed to upload image. Please try again.');
                setUploadingImage(false);
                return;
            }
            setUploadingImage(false);
        }

        const studentData = {
            ...formData,
            dateOfBirth: new Date(formData.dateOfBirth),
            profileImage: profileImageUrl,
            academicInfo: {
                ...formData.academicInfo,
                admissionDate: new Date(formData.academicInfo.admissionDate),
            },
        };

        // Remove selectedImageFile which is only for form state
        const { selectedImageFile, ...submittedData } = studentData;

        onSubmit(submittedData);
    };

    return (
        <Modal
            isOpen={true}
            onClose={onCancel}
            title={student ? 'Edit Student' : 'Add New Student'}
            size="full"
        >
            {/* Loading Overlay */}
            {(loading || uploadingImage) && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl shadow-lg p-6 flex items-center space-x-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                        <div className="text-gray-700 font-medium">
                            {uploadingImage
                                ? 'Uploading image...'
                                : student ? 'Updating student...' : 'Creating student account...'
                            }
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className={`space-y-8 relative ${(loading || uploadingImage) ? 'pointer-events-none opacity-50' : ''}`}>
                {/* Basic Information */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                        <Users className="h-6 w-6 mr-3 text-emerald-600" />
                        Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Student ID <span className="text-red-500">*</span>
                                {student && (
                                    <span className="text-xs text-gray-500 ml-1">(Cannot be changed)</span>
                                )}
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="studentId"
                                    value={formData.studentId}
                                    onChange={handleInputChange}
                                    placeholder="Enter unique student ID"
                                    required
                                    readOnly={!!student}
                                    disabled={checkingStudentId}
                                    className={`w-full px-3 py-2.5 pr-10 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${student
                                        ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                                        : studentIdError
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300 focus:ring-emerald-500 focus:border-transparent'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                />
                                {!student && checkingStudentId && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                                    </div>
                                )}
                                {!student && !checkingStudentId && studentIdError && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                )}
                                {!student && !checkingStudentId && !studentIdError && studentIdValid && formData.studentId.trim() && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                    </div>
                                )}
                            </div>
                            {!student && studentIdError && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {studentIdError}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email <span className="text-red-500">*</span>
                                {student && (
                                    <span className="text-xs text-gray-500 ml-1">(Cannot be changed)</span>
                                )}
                            </label>
                            <div className="relative">
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter email address"
                                    required
                                    readOnly={!!student}
                                    disabled={checkingEmail}
                                    className={`w-full px-3 py-2.5 pr-10 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${student
                                        ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                                        : emailError
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300 focus:ring-emerald-500 focus:border-transparent'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                />
                                {!student && checkingEmail && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                                    </div>
                                )}
                                {!student && !checkingEmail && emailError && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                )}
                                {!student && !checkingEmail && !emailError && emailValid && formData.email.trim() && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                    </div>
                                )}
                            </div>
                            {!student && emailError && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {emailError}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Phone <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date of Birth <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Gender <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            >
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        {/* Profile Image Upload */}
                        <div className="md:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Profile Image</label>
                            <div className="flex items-center space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden">
                                        {imagePreview || formData.profileImage ? (
                                            <img
                                                src={imagePreview || formData.profileImage}
                                                alt="Profile"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <ImageIcon className="h-8 w-8 text-gray-400" />
                                        )}
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="hidden"
                                        id="profileImage"
                                    />
                                    <label
                                        htmlFor="profileImage"
                                        className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                    >
                                        {uploadingImage ? (
                                            <>
                                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="h-4 w-4 mr-2" />
                                                Upload Image
                                            </>
                                        )}
                                    </label>
                                    <p className="text-xs text-gray-500 mt-1">JPG, PNG or GIF (Max 5MB)</p>
                                </div>
                            </div>
                        </div>

                        {!student && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Password <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder="Create a secure password"
                                            required={!student}
                                            className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirm Password <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            placeholder="Confirm your password"
                                            required={!student}
                                            className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </Card>

                {/* Address Information */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Address Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Street Address <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="address.street"
                                value={formData.address.street}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                City <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="address.city"
                                value={formData.address.city}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                State <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="address.state"
                                value={formData.address.state}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                ZIP Code <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="address.zipCode"
                                value={formData.address.zipCode}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Country <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="address.country"
                                value={formData.address.country}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                    </div>
                </Card>

                {/* Parent Information */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Parent Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Father's Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="parentInfo.fatherName"
                                value={formData.parentInfo.fatherName}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Mother's Name
                            </label>
                            <input
                                type="text"
                                name="parentInfo.motherName"
                                value={formData.parentInfo.motherName}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Father's Phone <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="tel"
                                name="parentInfo.fatherPhone"
                                value={formData.parentInfo.fatherPhone}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Father's Email
                            </label>
                            <input
                                type="email"
                                name="parentInfo.fatherEmail"
                                value={formData.parentInfo.fatherEmail}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Father's Occupation <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="parentInfo.fatherOccupation"
                                value={formData.parentInfo.fatherOccupation}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Father's Work Place <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="parentInfo.fatherWorkPlace"
                                value={formData.parentInfo.fatherWorkPlace}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Mother's Occupation
                            </label>
                            <input
                                type="text"
                                name="parentInfo.motherOccupation"
                                value={formData.parentInfo.motherOccupation}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Mother's Work Place
                            </label>
                            <input
                                type="text"
                                name="parentInfo.motherWorkPlace"
                                value={formData.parentInfo.motherWorkPlace}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                    </div>
                </Card>

                {/* Academic Information */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                        <GraduationCap className="h-6 w-6 mr-3 text-emerald-600" />
                        Academic Information
                    </h3>

                    {/* Current Academic Info */}
                    <div className="mb-8">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Current Academic Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Current Grade <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="academicInfo.currentGrade"
                                    value={formData.academicInfo.currentGrade}
                                    onChange={handleInputChange}
                                    placeholder="e.g., 10th, 12th, Grade 5"
                                    required
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Section <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="academicInfo.section"
                                    value={formData.academicInfo.section}
                                    onChange={handleInputChange}
                                    placeholder="e.g., A, B, Science"
                                    required
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Roll Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="academicInfo.rollNumber"
                                    value={formData.academicInfo.rollNumber}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Admission Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="academicInfo.admissionDate"
                                    value={formData.academicInfo.admissionDate}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="academicInfo.status"
                                    value={formData.academicInfo.status}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="graduated">Graduated</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Grade History Management */}
                    <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Academic Grade History</h4>
                        <p className="text-sm text-gray-600 mb-6">
                            Add completed grades, promotions, and examination results. This helps track student progress over time.
                        </p>

                        {/* Add New Grade Form */}
                        <div className="bg-gray-50 rounded-xl p-6 mb-6">
                            <h5 className="font-medium text-gray-900 mb-4">Add New Grade Record</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                                <Input
                                    label="Grade"
                                    name="grade"
                                    value={newGrade.grade}
                                    onChange={handleNewGradeChange}
                                    placeholder="e.g., 9th, 10th, Grade 3"
                                />
                                <Input
                                    label="Academic Year"
                                    name="academicYear"
                                    value={newGrade.academicYear}
                                    onChange={handleNewGradeChange}
                                    placeholder="e.g., 2023-2024"
                                />
                                <Input
                                    label="Section"
                                    name="section"
                                    value={newGrade.section}
                                    onChange={handleNewGradeChange}
                                    placeholder="e.g., A, B, Science"
                                />
                                <Input
                                    label="Roll Number"
                                    name="rollNumber"
                                    value={newGrade.rollNumber}
                                    onChange={handleNewGradeChange}
                                />
                                <Select
                                    label="Status"
                                    name="status"
                                    value={newGrade.status}
                                    onChange={handleNewGradeChange}
                                    options={[
                                        { value: 'completed', label: 'Completed' },
                                        { value: 'promoted', label: 'Promoted' },
                                        { value: 'failed', label: 'Failed' }
                                    ]}
                                />
                                <Input
                                    label="Total Marks"
                                    name="marks.totalMarks"
                                    type="number"
                                    value={newGrade.marks.totalMarks}
                                    onChange={handleNewGradeChange}
                                />
                                <Input
                                    label="Obtained Marks"
                                    name="marks.obtainedMarks"
                                    type="number"
                                    value={newGrade.marks.obtainedMarks}
                                    onChange={handleNewGradeChange}
                                />
                                <div className="flex items-end">
                                    <div className="w-full">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Percentage</label>
                                        <div className="px-3 py-2.5 border border-gray-300 rounded-xl bg-gray-100 text-gray-600">
                                            {newGrade.marks.totalMarks > 0
                                                ? `${Math.round((newGrade.marks.obtainedMarks / newGrade.marks.totalMarks) * 100 * 100) / 100}%`
                                                : '0%'
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                                <textarea
                                    name="remarks"
                                    value={newGrade.remarks}
                                    onChange={handleNewGradeChange}
                                    placeholder="Any additional notes or remarks..."
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                    rows={3}
                                />
                            </div>
                            <Button
                                type="button"
                                onClick={addGrade}
                                className="flex items-center space-x-2"
                            >
                                <Plus className="h-4 w-4" />
                                <span>Add Grade Record</span>
                            </Button>
                        </div>

                        {/* Grade History List */}
                        {formData.academicInfo.gradeHistory.length > 0 && (
                            <div>
                                <h5 className="font-medium text-gray-900 mb-4">Grade History</h5>
                                <div className="space-y-3">
                                    {formData.academicInfo.gradeHistory.map((grade: any, index: number) => (
                                        <div key={index} className="bg-white border border-gray-200 rounded-xl p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-700">Grade:</span>
                                                            <p className="text-sm text-gray-900">{grade.grade}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-700">Academic Year:</span>
                                                            <p className="text-sm text-gray-900">{grade.academicYear}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-700">Status:</span>
                                                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${grade.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                                grade.status === 'promoted' ? 'bg-blue-100 text-blue-800' :
                                                                    'bg-red-100 text-red-800'
                                                                }`}>
                                                                {grade.status}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-700">Percentage:</span>
                                                            <p className="text-sm text-gray-900">{grade.marks.percentage}%</p>
                                                        </div>
                                                    </div>
                                                    {grade.remarks && (
                                                        <div className="mt-2">
                                                            <span className="text-sm font-medium text-gray-700">Remarks:</span>
                                                            <p className="text-sm text-gray-600">{grade.remarks}</p>
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeGrade(index)}
                                                    className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Card>

                {/* Emergency Contact */}
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Emergency Contact</h3>
                    <p className="text-sm text-gray-500 mb-6">Optional - Can be added later</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contact Name
                            </label>
                            <input
                                type="text"
                                name="emergencyContact.name"
                                value={formData.emergencyContact.name}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Relationship
                            </label>
                            <input
                                type="text"
                                name="emergencyContact.relationship"
                                value={formData.emergencyContact.relationship}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Phone
                            </label>
                            <input
                                type="text"
                                name="emergencyContact.phone"
                                value={formData.emergencyContact.phone}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email
                            </label>
                            <input
                                type="email"
                                name="emergencyContact.email"
                                value={formData.emergencyContact.email}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                    </div>
                </Card>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        loading={loading || uploadingImage || checkingStudentId || checkingEmail}
                        disabled={loading || uploadingImage || checkingStudentId || checkingEmail || !!studentIdError || !!emailError}
                    >
                        {loading ? 'Saving...' : student ? 'Update Student' : 'Add Student'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default StudentForm;
